import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/observable/throw';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/take';
import { AppConfigService } from '../../services/app-config.service';
import { AuthTokenService } from '../services/auth-token.service';

/**
 * Intercepte les réponses `401` des appels vers le backend LIDIA : tente un rafraîchissement du
 * token (`rest/auth/refresh/`) puis rejoue la requête initiale une seule fois.
 *
 * Les rafraîchissements concurrents (plusieurs requêtes en `401` en même temps) sont mutualisés :
 * un seul appel `refresh` est déclenché, les autres requêtes en attendent l'issue avant de
 * rejouer leur appel initial.
 *
 * Les appels de refresh, de logout et de connexion ne sont jamais intercéptés ici, afin d'éviter
 * toute boucle infinie et de laisser remonter les erreurs d'identifiants (cf. `NO_REFRESH_PATHS`).
 */
@Injectable()
export class AuthRefreshInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshDone$ = new Subject<void>();

  constructor(
    private readonly authTokenService: AuthTokenService,
    private readonly router: Router
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).catch((error: any) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401 || this.isAuthEndpoint(req.url)) {
        return Observable.throw(error);
      }

      return this.handleUnauthorized(req, next);
    });
  }

  private handleUnauthorized(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      // Un rafraîchissement est déjà en cours : on attend son issue avant de rejouer la requête.
      return this.refreshDone$.take(1).switchMap(() => next.handle(req));
    }

    this.isRefreshing = true;
    const waiters$ = this.refreshDone$;
    this.refreshDone$ = new Subject<void>();

    return this.authTokenService.refresh()
      .switchMap(() => {
        this.isRefreshing = false;
        waiters$.next();
        waiters$.complete();
        return next.handle(req);
      })
      .catch((refreshError: any) => {
        this.isRefreshing = false;
        this.authTokenService.clearLocalSession();
        this.router.navigate(['/home']);
        waiters$.error(refreshError);
        return Observable.throw(refreshError);
      });
  }

  /**
   * Endpoints pour lesquels un `401` ne doit jamais déclencher de rafraîchissement :
   * - `rest/auth/refresh/` : éviterait une boucle infinie ;
   * - `rest/auth/logout/` : la session est de toute façon en cours d'invalidation ;
   * - `rest/auth/connect/*` : un `401` signifie identifiants invalides, pas session expirée.
   *
   * Les autres endpoints sous `rest/auth/` (ex. `rest/auth/notaire/info/`) sont des appels
   * métier authentifiés et doivent bénéficier du rafraîchissement.
   */
  private static readonly NO_REFRESH_PATHS = ['rest/auth/refresh/', 'rest/auth/logout/', 'rest/auth/connect/'];

  private isAuthEndpoint(url: string): boolean {
    const urlServeur = AppConfigService.settings ? AppConfigService.settings.urlServeur : '';
    return AuthRefreshInterceptor.NO_REFRESH_PATHS
      .some(path => url.indexOf(`${urlServeur}${path}`) === 0);
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/interceptors/auth-refresh.interceptor.ts